import React from "react";
import { useNavigate } from "react-router-dom";
import AWSReportsTemplate from "@/components/templates/AWSReportsTemplate";
import AWSReportsCards from "@/components/organisms/AWSReportsCards";

function AWSReports() {
  const navigate = useNavigate();

  const reportSections = [
    {
      title: "Artificial Intelligence & Machine Learning",
      description: "Generative AI, foundation models, and machine learning services",
      icon: "🤖",
      reports: [
        {
          title: "Kiro",
          description: "Kiro subscription analytics: plan types, active and pending users, license utilization, and subscription status overview",
          action: () => navigate("/aws/reports/kiro"),
          enabled: true,
          icon: "/AWS_Kiro_Icon.svg",
        },
        {
          title: "Bedrock",
          description: "Amazon Bedrock metrics: foundation model usage, inference requests, token consumption, model customization jobs, and agent execution analytics",
          action: () => navigate("/aws/reports/bedrock"),
          enabled: true,
          icon: "/AWS_Bedrock_Icon.png",
        },
        {
          title: "Cowork",
          description: "Claude Cowork Bedrock: API keys associated with accounts and registered costs",
          action: () => navigate("/aws/reports/claude-cowork-bedrock"),
          enabled: true,
          icon: "/AWS_Claude_AI_Icon.png",
          iconClassName: "w-12 h-8",
        },
        {
          title: "Devops Agent",
          description: "AWS DevOps Agent inventory: agent spaces, agent names, descriptions, guardrail identifiers, KMS key configuration, creation and update timestamps by account and region",
          action: () => navigate("/aws/reports/devops-agent"),
          enabled: true,
          icon: "/AWS_DevopsAgent_Icon.svg",
        },
        {
          title: "Quick",
          description: "Amazon QuickSight insights: active accounts, subscription editions, registered users per account, user roles, email addresses, and identity types across all QuickSight-enabled accounts",
          action: () => navigate("/aws/reports/quick"),
          enabled: true,
          icon: "/AWS_Quick_Icon.png",
        },
        {
          title: "SageMaker",
          description: "Amazon SageMaker analytics: training jobs, endpoint deployments, model hosting metrics, notebook instances, and ML pipeline execution status",
          action: () => navigate("/aws/reports/sagemaker"),
          enabled: true,
          icon: "/AWS_SageMaker_Icon.png",
        },
        
        {
          title: "Datadog",
          description: "Comming soon",
          action: () => navigate("/aws/reports/datadog-aws-console"),
          enabled: false,
          icon: "/Datadog_Icon.png",
        },
      ]
    },
    {
      
      title: "Identity & Access Management",
      description: "User authentication, authorization, permissions, and access control",
      icon: "🔐",
      reports: [
        {
          title: "Accounts",
          description: "AWS Organizations inventory: account hierarchy, organizational units (OUs), account metadata, creation dates, OPCO assignments, and account ownership",
          action: () => navigate("/aws/reports/accounts"),
          enabled: true,
          icon: "/AWS_Organizations_Icon.png",
        },
        {
          title: "IAM",
          description: "Identity and Access Management audit: IAM users, groups, roles, access keys status, attached policies, and permission boundaries by account",
          action: () => navigate("/aws/reports/iam"),
          enabled: true,
          icon: "/AWS_IdentityandAccessManagement_Icon.png",
        },
        {
          title: "Active Directory",
          description: "AWS Directory Service integration: AD users, security groups, group memberships, and federated access permissions by account",
          action: () => navigate("/aws/reports/ad"),
          enabled: true,
          icon: "🏢",
        },
        {
          title: "Console Access Control",
          description: "AWS Console access audit: user login activity, session tracking, console access patterns, and authentication events monitoring",
          action: () => navigate("/aws/reports/datadog-aws-console"),
          enabled: true,
          icon: "/Datadog_Icon.png",
        },
        {
          title: "IT Compliance",
          description: "Audit compliance forms: AM-4 and AM-6 reports containing user and group permissions within AWS console for audit purposes",
          action: () => navigate("/aws/reports/it-compliance"),
          enabled: true,
          icon: "🔍",
        },
      ]
    },
    {
      title: "Infrastructure & Resources",
      description: "Compute, storage, and networking resources",
      icon: "☁️",
      reports: [
        {
          title: "EC2",
          description: "Elastic Compute Cloud inventory: instance metadata, AMI details, instance types, network configuration, security groups, and lifecycle state",
          action: () => navigate("/aws/reports/ec2"),
          enabled: true,
          icon: "/AWS_EC2_Icon.png",
        },
        {
          title: "S3",
          description: "Simple Storage Service audit: bucket inventory, access policies, encryption status, versioning configuration, and public access controls",
          action: () => navigate("/aws/reports/s3"),
          enabled: true,
          icon: "/AWS_SimpleStorageService_Icon.png",
        },
        {
          title: "RDS",
          description: "Database inventory by account: instance ID, engine type, source configuration, backup status, and retention policies",
          action: () => navigate("/aws/reports/rds"),
          enabled: true,
          icon: "/AWS_RDS_Icon.png",
        },
        {
          title: "EFS",
          description: "Elastic File System inventory: file system ID, backup policy status, creation date, and storage size in GB",
          action: () => navigate("/aws/reports/efs"),
          enabled: true,
          icon: "/AWS_EFS_Icon.png",
        },
        {
          title: "Lambda Functions",
          description: "Serverless function inventory by account: function name, runtime environment, memory allocation, and timeout configuration",
          action: () => navigate("/aws/reports/lambda"),
          enabled: true,
          icon: "/AWS_Lambda_Icon.png",
        },
        {
          title: "EKS",
          description: "Elastic Kubernetes Service inventory: cluster configuration, Kubernetes version, VPC networking, endpoint access controls, node group details, capacity types, and auto-scaling configuration",
          action: () => navigate("/aws/reports/eks"),
          enabled: true,
          icon: "/AWS_EKS_Icon.png",
        },
        {
          title: "Networking",
          description: "Network connectivity inventory: Local Gateway (LGW) connections, Transit Gateway (TGW) attachments, Virtual Private Gateway (VGW) to Direct Connect, VPC and subnet topology, and Direct Connect virtual interfaces",
          action: () => navigate("/aws/reports/networking"),
          enabled: true,
          icon: "/aws-logo.png",
        },
      ]
    },
    
    {
      title: "Cost Management & Optimization",
      description: "Financial analysis and resource optimization",
      icon: "💰",
      reports: [
        {
          title: "Monthly Costs",
          description: "Financial analysis: monthly service costs breakdown by account and OPCO, with detailed spend categorization",
          action: () => navigate("/aws/reports/monthly-costs"),
          enabled: true,
          icon: "/AWS_CostExplorer_Icon.png",
        },
        {
          title: "Cost Intelligence",
          description: "AWS Cost Intelligence Dashboard: advanced cost analytics, anomaly detection, rightsizing recommendations, and Reserved Instance optimization",
          action: () => navigate("/aws/reports/cost-intelligence"),
          enabled: true,
          icon: "/aws-logo.png",
        },
        {
          title: "Compute Optimizer",
          description: "AWS Compute Optimizer recommendations: EC2 instance rightsizing, Auto Scaling group optimization, and EBS volume performance analysis",
          action: () => navigate("/aws/reports/compute-optimizer"),
          enabled: true,
          icon: "/AWS_ComputeOptimizer_Icon.png",
        },
      ]
    },
    {
      title: "Monitoring & Operations",
      description: "Health monitoring and operational insights",
      icon: "📊",
      reports: [
        {
          title: "Health Events",
          description: "AWS Personal Health Dashboard: service health events, scheduled maintenance, security notifications, and resource-specific alerts",
          action: () => navigate("/aws/reports/aws-health-events"),
          enabled: true,
          icon: "/AWS_Health_Icon.png",
        },
        {
          title: "Trusted Advisor",
          description: "AWS Trusted Advisor organizational view: cost optimization, security recommendations, fault tolerance, performance insights, and service limits",
          action: () => navigate("/aws/reports/trusted-advisor-organizational"),
          enabled: true,
          icon: "/AWS_TrustedAdvisor_Icon.png",
        },
      ]
    },
    {
      title: "Analytics & Performance",
      description: "Business intelligence and operational performance metrics",
      icon: "📈",
      reports: [
        {
          title: "KPI Dashboard",
          description: "Key Performance Indicators: cloud adoption metrics, cost efficiency ratios, security compliance scores, and operational excellence benchmarks",
          action: () => navigate("/aws/reports/kpi"),
          enabled: true,
          icon: "/aws-logo.png",
        },
      ]
    },
   
  ];

  return (
    <AWSReportsTemplate title="AWS Reports" showBackButton={false}>
      <div className="space-y-8">
        {reportSections.map((section, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{section.icon}</span>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{section.title}</h2>
                  <p className="text-sm text-gray-600">{section.description}</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <AWSReportsCards reportCards={section.reports} />
            </div>
          </div>
        ))}
      </div>
    </AWSReportsTemplate>
  );
}

export default AWSReports;